import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '../plugs/current-user';
import { useCreateAvailabilityWindow } from './api/mutations';
import type { AvailabilityBlockDialogValues } from './components/maintenance-block-dialog';
import type { ActiveDialog } from './components/reservation-action-dialogs';
import { ReservationListView } from './components/reservation-list-view';
import type { ReservationListItem } from './components/reservation-list-view';
import type { QueueEntryView } from './components/reservation-queue-list';
import { useQueueActions } from './hooks/use-reservation-queue-panel';
import type { ReservationPermissions } from './hooks/use-reservation-permissions';
import { computeForceFlag } from './hooks/use-reservation-permissions';
import { useReservationActions } from './hooks/use-reservation-actions';
import { useReservationsListHandlers } from './hooks/use-reservations-list-handlers';
import { buildReservationListLabels, buildReservationQueueLabels } from './lib/reservation-labels';
import {
  buildReservationFilterFields,
  buildReservationFilterValues,
  formatSortFieldLabel,
} from './lib/reservations-list-search';
import type {
  ReservationFilterOptions,
  ReservationsSearch,
  ReservationsSearchChange,
} from './lib/reservations-list-search';
import { resolvePromoteTarget } from './lib/resolve-promote-target';
import { ReservationDialogsHost } from './reservation-dialogs-host';

type Translate = (key: string, opts?: Record<string, unknown>) => string;

export interface ReservationsListBodyProps {
  rows: ReservationListItem[];
  total: number;
  search: ReservationsSearch;
  onSearchChange: ReservationsSearchChange;
  filterOptions: ReservationFilterOptions;
  permissions: ReservationPermissions;
  queueByReservationId: Record<number, QueueEntryView[]>;
  mediaUnitIdByReservationId: Map<number, number>;
}

function useActionDialog(rows: ReservationListItem[], permissions: ReservationPermissions) {
  const { user } = useCurrentUser();
  const [activeDialog, setActiveDialog] = useState<ActiveDialog>(null);
  const [activeReservationId, setActiveReservationId] = useState<number | null>(null);
  const activeReservation = rows.find((r) => r.id === activeReservationId) ?? null;
  const { dispatch, isPending } = useReservationActions();

  const open = useCallback((action: string, id: number) => {
    setActiveReservationId(id);
    setActiveDialog(action as ActiveDialog);
  }, []);
  const close = useCallback(() => setActiveDialog(null), []);
  const confirm = useCallback(
    (action: string, dialogData?: Record<string, unknown>) => {
      if (!activeReservation) return;
      dispatch(
        action,
        {
          id: activeReservation.id,
          status: activeReservation.status,
          unitId: activeReservation.mediaUnitId,
          force: computeForceFlag(activeReservation, user?.id ?? 0, permissions.canForceRelease),
        },
        dialogData,
        { onSuccess: close },
      );
    },
    [activeReservation, dispatch, user?.id, permissions.canForceRelease, close],
  );

  return { activeDialog, activeReservation, isPending, open, close, confirm };
}

function useAvailabilityBlock() {
  const [unitId, setUnitId] = useState<number | null>(null);
  const create = useCreateAvailabilityWindow();
  const save = useCallback(
    (values: AvailabilityBlockDialogValues) => {
      if (unitId === null) return;
      create.mutate({ unitId, ...values }, { onSuccess: () => setUnitId(null) });
    },
    [unitId, create],
  );
  return {
    isOpen: unitId !== null,
    isPending: create.isPending,
    open: setUnitId,
    close: () => setUnitId(null),
    save,
  };
}

export function ReservationsListBody({
  rows,
  total,
  search,
  onSearchChange,
  filterOptions,
  permissions,
  queueByReservationId,
  mediaUnitIdByReservationId,
}: ReservationsListBodyProps) {
  const { t } = useTranslation('reservations');
  const translate = t as Translate;
  const { user } = useCurrentUser();
  const currentUserId = user?.id ?? 0;

  const queue = useQueueActions(mediaUnitIdByReservationId);
  const actionDialog = useActionDialog(rows, permissions);
  const availability = useAvailabilityBlock();
  const handlers = useReservationsListHandlers(search, onSearchChange, currentUserId);

  const promoteTarget = resolvePromoteTarget(
    queue.promoteTarget,
    rows,
    queueByReservationId,
    rows.find((r) => r.id === queue.promoteTarget?.reservationId)?.mediaUnitName ?? '',
  );

  const filterFields = useMemo(
    () =>
      buildReservationFilterFields(
        t,
        filterOptions.offers,
        filterOptions.clients,
        filterOptions.salespeople,
      ),
    [t, filterOptions],
  );

  return (
    <>
      <ReservationListView
        items={rows}
        filterFields={filterFields}
        filterValues={buildReservationFilterValues(search)}
        filterLabels={{
          allLabel: t('filters.clearAll'),
          searchPlaceholder: t('filters.searchLabel'),
          getSelectedCountLabel: (count) => String(count),
          getClearAriaLabel: (fieldId) => fieldId,
          clearAllLabel: t('filters.clearAll'),
          addFilterLabel: t('filters.addFilter'),
        }}
        onFilterChange={handlers.changeFilter}
        sortBy={search.sort}
        sortDir={search.order}
        onSort={handlers.changeSort}
        sortLabel={t('labels.sort', {
          field: formatSortFieldLabel(t, search.sort),
          dir: search.order === 'asc' ? '↑' : '↓',
        })}
        page={Math.floor(search.offset / search.limit) + 1}
        pageSize={search.limit}
        totalCount={total}
        onPageChange={handlers.changePage}
        onAction={actionDialog.open}
        onBlockAvailability={availability.open}
        permissions={permissions}
        myReservationsActive={handlers.myReservationsActive}
        onToggleMyReservations={handlers.toggleMyReservations}
        myReservationsLabel={t('filters.myReservations')}
        labels={buildReservationListLabels(translate)}
        currentUserId={currentUserId}
        queueByReservationId={queueByReservationId}
        queueLabels={buildReservationQueueLabels(translate)}
        onQueueAction={queue.handleQueueAction}
        initialExpandedQueueId={search.expand ?? null}
        hasQueueActive={search.hasQueue}
        onToggleHasQueue={handlers.toggleHasQueue}
        hasQueueLabel={t('queueOverview.hasQueueLabel')}
      />
      <ReservationDialogsHost
        action={{
          dialog: actionDialog.activeDialog,
          target: actionDialog.activeReservation,
          isAdmin: permissions.isAdmin,
          isPending: actionDialog.isPending,
          onClose: actionDialog.close,
          onConfirm: actionDialog.confirm,
        }}
        availability={{
          isOpen: availability.isOpen,
          isPending: availability.isPending,
          onClose: availability.close,
          onSave: availability.save,
        }}
        promote={{
          target: promoteTarget,
          isPending: queue.isPromotePending,
          onClose: () => queue.setPromoteTarget(null),
          onConfirm: queue.handlePromoteConfirm,
        }}
      />
    </>
  );
}
